import React, { useState } from "react";
import { useDoctorSpecialties } from "../../hooks/useDoctorSpecialties";
import { useDoctorData } from "../../hooks/useDoctorData";
import { useAppointmentMutations } from "../../hooks/useAppointmentMutations";

const CreateAppointmentForm: React.FC = () => {
  const [doctorId, setDoctorId] = useState<number>(0);
  const [appointmentDateTime, setAppointmentDateTime] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const { data: specialties = [], isLoading: loadingSpecs } = useDoctorSpecialties();
  const { doctors, loading: loadingDoctors } = useDoctorData();
  const { createAppointment } = useAppointmentMutations();

  const filteredDoctors = doctors.filter(
    (doc) =>
      doc &&
      doc.specialty &&
      doc.specialty.toLowerCase().trim() ===
      selectedSpecialty.toLowerCase().trim()
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccess(false);

    const now = new Date();
    const selectedDate = new Date(appointmentDateTime);

    if (selectedDate <= now) {
      setLocalError("La date du rendez-vous doit être dans le futur.");
      return;
    }

    createAppointment.mutate({
      doctorId,
      appointmentDateTime,
      reason,
    }, {
      onSuccess: () => {
        setSuccess(true);
        setDoctorId(0);
        setAppointmentDateTime("");
        setReason("");
        setSelectedSpecialty("");
      },
      onError: (err: any) => {
        setLocalError(err.response?.data?.message || "Une erreur est survenue lors de la création du rendez-vous.");
      }
    });
  };

  const error = localError || (createAppointment.error as any)?.message;
  const isLoading = createAppointment.isPending || loadingSpecs || (!!selectedSpecialty && loadingDoctors);

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl">
        <div className="p-6 sm:p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
              Prendre un rendez-vous
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Sélectionnez un médecin et choisissez une date convenable
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 sm:p-4 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 sm:p-4 bg-green-50 text-green-700 rounded-lg">
              Rendez-vous créé avec succès !
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Spécialité
              </label>
              <select
                value={selectedSpecialty}
                onChange={(e) => {
                  setSelectedSpecialty(e.target.value);
                  setDoctorId(0);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Sélectionnez une spécialité</option>
                {specialties
                  .map((spec) => (
                    <option key={spec} value={spec}>
                      {spec}
                    </option>
                  ))}
              </select>
            </div>

            {selectedSpecialty && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Médecin
                </label>
                <select
                  value={doctorId}
                  onChange={(e) => setDoctorId(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value={0}>Sélectionnez un médecin</option>
                  {filteredDoctors
                    .filter((doc) => doc && doc.id)
                    .map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        Dr. {doc.firstName} {doc.lastName}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date et heure
              </label>
              <input
                type="datetime-local"
                value={appointmentDateTime}
                onChange={(e) => setAppointmentDateTime(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motif de consultation
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Décrivez brièvement la raison de votre visite"
                required
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    En cours...
                  </>
                ) : (
                  "Prendre rendez-vous"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateAppointmentForm;
